import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/avatar";
import { Bell, ArrowLeft, LogOut, Menu, User, Moon, Sun, RefreshCw, Save, Shield, Search } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useNotifications } from '../contexts/NotificationContext';
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "src/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { Switch } from "src/components/ui/switch";
import { fetchWithAuth } from "../utils/api";
import { motion } from "framer-motion";
import { fallbackAvatars } from "../config/avatar";
import { UserAvatar } from "../components/UserAvatar";
import { fetchAvatarOptions, AvatarOption } from "../utils/avatarUtils";
import { supabase, isSupabaseConfigured } from "../config/supabase";
import pushNotificationService from '../services/push-notification';
import './AccountSettings.css';

interface UserProfile {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url: string;
  preferences: {
    theme: 'dark' | 'light' | 'system';
    notifications_enabled: boolean;
  };
}

const AccountSettings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("profile");
  const { pageTransition, toggleTheme, theme } = useTheme();
  const { notifications, markAsRead, markAsUnread, markAllAsRead } = useNotifications();
  const [showSidebar, setShowSidebar] = useState(false);
  const [showAvatarDialog, setShowAvatarDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [avatarOptions, setAvatarOptions] = useState<AvatarOption[]>([]);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // User profile state
  const [userProfile, setUserProfile] = useState<UserProfile>({
    id: 0,
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    avatar_url: "",
    preferences: {
      theme: 'dark',
      notifications_enabled: true
    }
  });
  
  const [formData, setFormData] = useState({
    username: "",
    avatar_url: "",
    first_name: "",
    last_name: "",
    email: "",
    preferences: {
      theme: 'dark',
      notifications_enabled: true
    }
  });

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      // Get the username from localStorage
      const username = localStorage.getItem("username");
      const storedAvatarUrl = localStorage.getItem("avatar_url");
      
      if (!username) {
        toast.error("User not authenticated");
        navigate("/login");
        return;
      }
      
      let userData = null;
      let avatarUrl = storedAvatarUrl || "";
      
      // Try multiple endpoints to get user data
      const endpoints = [
        `/users/${username}/`,
        `/users/profile/`,
        `/api/users/${username}/`,
        `/api/users/me/`
      ];
      
      for (const endpoint of endpoints) {
        try {
          console.log(`Trying to fetch user data from: ${endpoint}`);
          userData = await fetchWithAuth(endpoint);
          console.log(`Succeeded fetching from: ${endpoint}`, userData);
          
          if (userData) {
            break;
          }
        } catch (err) {
          console.log(`Failed to fetch from endpoint: ${endpoint}`, err);
        }
      }
      
      // If we still don't have an avatar URL, use a random fallback avatar
      avatarUrl = userData?.avatar_url || storedAvatarUrl || fallbackAvatars[Math.floor(Math.random() * fallbackAvatars.length)].url;
      
      // Set user profile with data from API or localStorage
      setUserProfile({
        id: userData?.id || 0,
        username: userData?.username || username,
        email: userData?.email || "",
        first_name: userData?.first_name || "",
        last_name: userData?.last_name || "",
        avatar_url: avatarUrl,
        preferences: {
          theme: userData?.preferences?.theme || theme,
          notifications_enabled: userData?.preferences?.notifications_enabled !== false
        }
      });
      
      // Set selected avatar
      setSelectedAvatar(avatarUrl);
        
    } catch (err: any) {
      console.error("Failed to fetch user profile:", err);
      toast.error("Failed to load profile");
      
      // Use username from localStorage as fallback
      const username = localStorage.getItem("username") || "User";
      const avatarUrl = localStorage.getItem("avatar_url") || fallbackAvatars[0].url;
      
      setUserProfile({
        ...userProfile,
        username: username,
        avatar_url: avatarUrl
      });
      setSelectedAvatar(avatarUrl);
      
    } finally {
      setLoading(false);
    }
  };
  
  // Function to load avatar options
  const loadAvatarOptions = useCallback(async () => {
    setAvatarLoading(true);
    try {
      const options = await fetchAvatarOptions();
      setAvatarOptions(options);
      
      // If user has an avatar_url, find it in the options
      if (userProfile.avatar_url) {
        setSelectedAvatar(userProfile.avatar_url);
      }
    } catch (error) {
      console.error("Error fetching avatars:", error);
    } finally {
      setAvatarLoading(false);
    }
  }, [userProfile.avatar_url]);

  // Load avatar options when component mounts
  useEffect(() => {
    loadAvatarOptions();
  }, [loadAvatarOptions]);

  // Handle avatar selection
  const handleAvatarClick = (avatar: AvatarOption) => {
    setSelectedAvatar(avatar.url);
    
    // Add the avatar URL to the form data
    setFormData(prev => ({
      ...prev,
      avatar_url: avatar.url
    }));
  };

  // Initialize active tab from route state if provided
  useEffect(() => {
    if (location.state && location.state.activeTab) {
      setActiveTab(location.state.activeTab);
    }
    
    // Fetch user profile data
    fetchUserProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const checkPushStatus = async () => {
      const isSubscribed = await pushNotificationService.checkSubscription();
      setPushEnabled(isSubscribed);
    };
    
    checkPushStatus();
  }, []);

  useEffect(() => {
    // Check if push is supported
    testPushNotificationSupport();
  }, []);

  // Add this function after your component declaration but before useEffect hooks
  const testPushNotificationSupport = () => {
    // Check if service workers and push messaging are supported
    const isPushSupported = 'PushManager' in window && 'serviceWorker' in navigator;
    const permissionStatus = Notification.permission;
    
    console.log("Push notification support:");
    console.log("- Browser supports Push API:", isPushSupported);
    console.log("- Current permission status:", permissionStatus);
    
    // Check service worker registration
    navigator.serviceWorker.getRegistrations().then(registrations => {
      console.log("- Service worker registrations:", registrations.length);
      for (let reg of registrations) {
        console.log("  • Scope:", reg.scope);
        reg.pushManager.getSubscription().then(subscription => {
          console.log("  • Has push subscription:", !!subscription);
        });
      }
    });
    
    setPushEnabled(isPushSupported && permissionStatus === 'granted');
    return isPushSupported && permissionStatus === 'granted';
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("username");
    navigate("/login");
  };
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };
  
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      // Prepare data for API
      const data = {
        username: userProfile.username,
        avatar_url: selectedAvatar || userProfile.avatar_url
      };
      
      console.log("Saving profile data:", data);
      
      // Try updating profile at different endpoints
      const endpoints = [
        `/users/${userProfile.username}/`,
        `/users/profile/`,
        `/api/users/${userProfile.username}/`,
        `/api/users/me/`
      ];
      
      let success = false;
      for (const endpoint of endpoints) {
        try {
          await fetchWithAuth(endpoint, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });
          
          console.log(`Successfully updated profile at endpoint: ${endpoint}`);
          success = true;
          break;
        } catch (err) {
          console.log(`Failed to update profile at endpoint: ${endpoint}`, err);
        }
      }
      
      // Update localStorage with new values
      localStorage.setItem("username", userProfile.username);
      localStorage.setItem("avatar_url", selectedAvatar || userProfile.avatar_url);
      
      if (success) {
        toast.success("Profile updated successfully");
      } else {
        // Even if API calls fail, we'll update local state so the UI reflects changes
        toast.success("Profile updated in browser");
      }
      
      // Close the avatar dialog if it's open
      setShowAvatarDialog(false);
    } catch (err: any) {
      console.error("Failed to save profile:", err);
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveAppearance = async () => {
    try {
      setSaving(true);
      
      // Prepare data for API (for future implementation)
      const data = {
        preferences: {
          ...userProfile.preferences,
        }
      };
      
      console.log("Would save appearance data:", data);
      
      // Simulate API call success - in a real app you'd save to backend
      setTimeout(() => {
        toast.success("Appearance settings saved");
        setSaving(false);
      }, 500);
      
    } catch (err: any) {
      console.error("Failed to save appearance settings:", err);
      toast.error("Failed to save settings: " + (err.message || "Unknown error"));
      setSaving(false);
    }
  };
  
  const handleToggleNotifications = (enabled: boolean) => {
    setUserProfile({
      ...userProfile,
      preferences: {
        ...userProfile.preferences,
        notifications_enabled: enabled
      }
    });
  };
  
  const handlePushToggle = async (enabled: boolean) => {
    try {
      setPushLoading(true);
      
      if (enabled) {
        const subscribed = await pushNotificationService.subscribe();
        setPushEnabled(subscribed);
        
        if (subscribed) {
          toast.success("Push notifications enabled");
        } else {
          toast.error("Failed to enable push notifications");
        }
        
      } else {
        await pushNotificationService.unsubscribe();
        setPushEnabled(false);
        toast.success("Push notifications disabled");
      }
      
    } catch (err) {
      console.error("Push notification error:", err);
      toast.error("Push notification error");
    } finally {
      setPushLoading(false);
    }
  };
  
  const handleSaveAccountSettings = () => {
    // This is a placeholder for future account settings functionality
    toast.success("Account settings saved");
  };

  // Display loading state if needed
  const renderProfileContent = () => {
    if (loading) {
      return (
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading profile information...</p>
        </div>
      );
    }
    
    return (
      <div className="profile-content">
        {/* Profile Picture */}
        <div className="profile-picture-container">
          <UserAvatar 
            username={userProfile.username}
            avatarUrl={userProfile.avatar_url}
            size="xl"
            className="profile-avatar"
          />
          <Button 
            variant="outline" 
            onClick={() => setShowAvatarDialog(true)}
            className="change-avatar-btn"
          >
            Change Avatar
          </Button>
        </div>

        <div className="profile-form">
          {/* Username (now editable) */}
          <div className="form-group">
            <label>Username</label>
            <Input 
              value={userProfile.username} 
              onChange={(e) => setUserProfile({...userProfile, username: e.target.value})}
              className="profile-input" 
            />
            <p className="form-hint">Choose a unique username for your account.</p>
          </div>

          {/* First Name Input (read-only) */}
          <div className="form-group">
            <label>First Name</label>
            <Input 
              value={userProfile.first_name} 
              readOnly
              className="profile-input readonly-input" 
            />
          </div>

          {/* Last Name Input (read-only) */}
          <div className="form-group">
            <label>Last Name</label>
            <Input 
              value={userProfile.last_name} 
              readOnly
              className="profile-input readonly-input" 
            />
          </div>

          {/* Email Input (read-only) */}
          <div className="form-group">
            <label>Email Address</label>
            <Input 
              value={userProfile.email} 
              readOnly
              className="profile-input readonly-input" 
            />
            <p className="form-hint">This email is used for notifications.</p>
          </div>

          {/* Save & Logout Buttons */}
          <div className="profile-actions">
            <Button 
              className="save-button"
              onClick={handleSaveProfile}
              disabled={saving}
            >
              {saving ? (
                <><RefreshCw className="button-icon animate-spin" /> Saving...</>
              ) : (
                <><Save className="button-icon" /> Save Changes</>
              )}
            </Button>
            <Button className="logout-button" onClick={handleLogout}>
              <LogOut className="button-icon" /> Logout
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // This function can be removed if not used elsewhere, or kept if needed for other logic
  const handleThemeSelection = (selectedTheme: 'light' | 'dark') => {
    if (theme !== selectedTheme) {
      toggleTheme(); // Call without arguments
    }
  };

  // Filter notifications based on search term
  const filteredNotifications = notifications.filter(notification => 
    notification.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderAvatarDialog = () => {
    return (
      <Dialog open={showAvatarDialog} onOpenChange={setShowAvatarDialog}>
        <DialogContent className="avatar-dialog">
          <DialogHeader>
            <DialogTitle>Select Avatar</DialogTitle>
          </DialogHeader>
          
          <div className="avatars-grid">
            {avatarLoading ? (
              <div className="loading-spinner">Loading avatars...</div>
            ) : (
              avatarOptions.map((avatar) => (
                <div
                  key={avatar.id}
                  className={`avatar-option ${selectedAvatar === avatar.url ? 'selected' : ''}`}
                  onClick={() => handleAvatarClick(avatar)}
                >
                  <img 
                    src={avatar.publicUrl || avatar.url} 
                    alt={`Avatar ${avatar.name}`} 
                    className="avatar-img"
                  />
                </div>
              ))
            )}
          </div>
          
          <div className="dialog-actions">
            <Button 
              onClick={handleSaveProfile}
              disabled={saving || avatarOptions.length === 0}
            >
              {saving ? (
                <><RefreshCw className="button-icon animate-spin" /> Saving...</>
              ) : (
                <><Save className="button-icon" /> Save Selection</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="account-settings">
      {/* Header */}
      <div className="settings-header">
        <div className="header-left">
          <button 
            className="back-button"
            onClick={() => {
              pageTransition(() => {
                navigate(-1);
              });
            }}
          >
            <ArrowLeft />
          </button>
          <h1 className="page-title">
            {activeTab === "profile" && "My Profile"}
            {activeTab === "security" && "Security Settings"}
            {activeTab === "appearance" && "Appearance Settings"}
            {activeTab === "notifications" && "Notification History"}
          </h1>
        </div>
        
        <div className="header-right">
          <Button 
            className="mobile-menu-button"
            onClick={() => setShowSidebar(!showSidebar)}
          >
            <Menu />
          </Button>
        </div>
      </div>

      <div className="settings-container">
        {/* Sidebar - uses similar styling to MotionGlossary */}
        <div className={`settings-sidebar ${showSidebar ? 'show' : ''}`}>
          <div className="sidebar-item" onClick={() => handleTabChange("profile")}>
            <User className={activeTab === "profile" ? "active" : ""} />
            <span>My Profile</span>
          </div>
          <div className="sidebar-item" onClick={() => handleTabChange("security")}>
            <Shield className={activeTab === "security" ? "active" : ""} />
            <span>Security</span>
          </div>
          <div className="sidebar-item" onClick={() => handleTabChange("appearance")}>
            {theme === 'dark' ? 
              <Moon className={activeTab === "appearance" ? "active" : ""} /> : 
              <Sun className={activeTab === "appearance" ? "active" : ""} />
            }
            <span>Appearance</span>
          </div>
          <div className="sidebar-item" onClick={() => handleTabChange("notifications")}>
            <Bell className={activeTab === "notifications" ? "active" : ""} />
            <span>Notifications</span>
          </div>
        </div>

        {/* Content Area */}
        <div className="settings-content">
          {/* Profile Section */}
          {activeTab === "profile" && (
            <div className="settings-card">
              <div className="card-header">
                <h2>My Profile</h2>
              </div>
              {renderProfileContent()}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === "notifications" && (
            <div className="settings-card">
              <div className="card-header">
                <h2>Notification History</h2>
                <Button 
                  className="mark-all-read"
                  onClick={markAllAsRead}
                >
                  Mark All as Read
                </Button>
              </div>
              
              <div className="search-container">
                <Search className="search-icon" />
                <Input 
                  placeholder="Search notifications..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              
              <div className="notifications-list">
                {filteredNotifications.map((notification) => (
                  <div 
                    key={notification.id} 
                    className={`notification-item ${!notification.read ? "unread" : ""}`}
                  >
                    <div className="notification-content">
                      <div className="notification-header">
                        {!notification.read && (
                          <span className="unread-indicator"></span>
                        )}
                        <p className="notification-message">
                          {notification.message}
                        </p>
                      </div>
                      <span className="notification-time">{notification.time}</span>
                    </div>
                    <Button 
                      className="mark-button"
                      onClick={() => notification.read ? markAsUnread(notification.id) : markAsRead(notification.id)}
                    >
                      {notification.read ? "Mark as Unread" : "Mark as Read"}
                    </Button>
                  </div>
                ))}
                
                {filteredNotifications.length === 0 && (
                  <div className="no-notifications">
                    <p>No notifications found</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Security Tab - Password Management */}
          {activeTab === "security" && (
            <div className="settings-card">
              <div className="card-header">
                <h2>Security Settings</h2>
              </div>
              
              <div className="security-content">
                <h3>Change Password</h3>
                
                <div className="security-form">
                  <div className="form-group">
                    <label>Current Password</label>
                    <Input 
                      type="password"
                      className="security-input"
                      placeholder="Enter your current password"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>New Password</label>
                    <Input 
                      type="password"
                      className="security-input"
                      placeholder="Enter new password"
                    />
                    <p className="form-hint">Password must be at least 8 characters long.</p>
                  </div>
                  
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <Input 
                      type="password"
                      className="security-input"
                      placeholder="Confirm new password"
                    />
                  </div>
                  
                  <div className="security-actions">
                    <Button className="update-password-button">Update Password</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Appearance Tab */}
          {activeTab === "appearance" && (
            <div className="settings-card">
              <div className="card-header">
                <h2>Appearance Settings</h2>
              </div>
              
              <div className="appearance-content">
                <h3>Theme</h3>
                
                <div className="theme-options">
                  <div 
                    className={`theme-option ${theme === 'light' ? 'selected' : ''}`}
                    onClick={() => handleThemeSelection('light')}
                  >
                    <div className="theme-preview light-preview">
                      <Sun size={24} />
                    </div>
                    <span>Light</span>
                  </div>
                  
                  <div 
                    className={`theme-option ${theme === 'dark' ? 'selected' : ''}`}
                    onClick={() => handleThemeSelection('dark')}
                  >
                    <div className="theme-preview dark-preview">
                      <Moon size={24} />
                    </div>
                    <span>Dark</span>
                  </div>
                </div>
                
                <div className="push-notification-toggle">
                  <div className="toggle-header">
                    <h3>Push Notifications</h3>
                    <p>Receive notifications even when you're not using the app</p>
                  </div>
                  
                  <div className="toggle-control">
                    <Switch 
                      checked={pushEnabled}
                      onCheckedChange={handlePushToggle}
                      disabled={pushLoading}
                    />
                    {pushLoading && <RefreshCw className="loading-icon animate-spin" />}
                  </div>
                </div>
                
                <div className="appearance-actions">
                  <Button 
                    className="save-button"
                    onClick={handleSaveAppearance}
                    disabled={saving}
                  >
                    {saving ? (
                      <><RefreshCw className="button-icon animate-spin" /> Saving...</>
                    ) : (
                      <><Save className="button-icon" /> Save Changes</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Avatar Selection Dialog */}
      {renderAvatarDialog()}
    </div>
  );
};

export default AccountSettings;
